/**
 * Outils purs sur les pistes d'une activité. Une piste est la trace d'un
 * participant : positions horodatées (epoch ms), triées par temps.
 */

export function toEpochMs(value) {
    if (value === null || value === undefined || value === '') return null;
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    const parsed = new Date(value).getTime();
    return Number.isFinite(parsed) ? parsed : null;
}

/** Dernier index i tel que positions[i].t <= t, ou -1. */
export function lastIndexAtOrBefore(positions, t) {
    let low = 0;
    let high = positions.length - 1;
    let found = -1;
    while (low <= high) {
        const mid = (low + high) >>> 1;
        if (positions[mid].t <= t) {
            found = mid;
            low = mid + 1;
        } else {
            high = mid - 1;
        }
    }
    return found;
}

/**
 * Position à l'instant t, interpolée linéairement entre deux points.
 * `status` vaut 'before' avant le premier point, 'after' après le dernier,
 * 'moving' entre les deux : l'interface peut ainsi distinguer un participant
 * pas encore parti d'un participant arrivé.
 */
export function positionAt(positions, t) {
    if (!Array.isArray(positions) || positions.length === 0 || !Number.isFinite(t)) return null;
    const first = positions[0];
    const last = positions[positions.length - 1];
    if (t < first.t) return { ...first, t, status: 'before' };
    if (t >= last.t) return { ...last, t, status: 'after' };

    const index = lastIndexAtOrBefore(positions, t);
    const p0 = positions[index];
    const p1 = positions[index + 1];
    const ratio = p1.t > p0.t ? (t - p0.t) / (p1.t - p0.t) : 0;
    return {
        lat: p0.lat + (p1.lat - p0.lat) * ratio,
        lng: p0.lng + (p1.lng - p0.lng) * ratio,
        accuracy: ratio < 0.5 ? p0.accuracy : p1.accuracy,
        inZone: ratio < 0.5 ? p0.inZone : p1.inZone,
        t,
        status: 'moving',
    };
}

/** Trajet parcouru jusqu'à t (points [lat, lng]), terminé par la position interpolée. */
export function trailUntil(positions, t) {
    if (!Array.isArray(positions) || positions.length === 0 || !Number.isFinite(t)) return [];
    const index = lastIndexAtOrBefore(positions, t);
    if (index < 0) return [];
    const trail = positions.slice(0, index + 1).map(p => [p.lat, p.lng]);
    const current = positionAt(positions, t);
    if (current && current.status === 'moving') trail.push([current.lat, current.lng]);
    return trail;
}

/** Normalise et trie une liste de points bruts { t|recordedAt, lat|latitude, lng|longitude }. */
export function normalizePositions(raw) {
    if (!Array.isArray(raw)) return [];
    return raw
        .map(point => ({
            t: toEpochMs(point.t ?? point.recordedAt ?? point.timestamp),
            lat: Number(point.lat ?? point.latitude),
            lng: Number(point.lng ?? point.longitude),
            accuracy: Number.isFinite(Number(point.accuracy)) && point.accuracy !== null ? Number(point.accuracy) : null,
            inZone: point.inZone === undefined ? null : Boolean(point.inZone),
        }))
        .filter(point => point.t !== null && Number.isFinite(point.lat) && Number.isFinite(point.lng)
            && Math.abs(point.lat) <= 90 && Math.abs(point.lng) <= 180)
        .sort((a, b) => a.t - b.t);
}

/** Bornes temporelles [startMs, endMs] couvrant toutes les données horodatées d'un bundle. */
export function bundleTimeRange(bundle) {
    const times = [];
    const push = value => { const t = toEpochMs(value); if (t !== null) times.push(t); };
    (bundle?.tracks || []).forEach(track => {
        (track.positions || []).forEach(p => push(p.t));
        (track.media || []).forEach(m => { push(m.startMs); push(m.endMs); });
        (track.photos || []).forEach(p => push(p.t));
    });
    (bundle?.messages || []).forEach(m => push(m.t));
    (bundle?.events || []).forEach(e => push(e.t));
    if (times.length === 0) return { startMs: null, endMs: null };
    return { startMs: Math.min(...times), endMs: Math.max(...times) };
}
